import axios, { CancelTokenSource } from "axios";
import * as React from "react";
import { getTreatmentById, getTreatmentData } from "services/treatmentServices";
import { BillStatus } from "ts-types/api.enums";
import { TreatmentDto, UpsertTreatmentDto } from "ts-types/api.types";
import { noop } from "utils/functionUtils";

export enum TreatmentMode {
  EDIT,
  COPY,
  DETAILS
}

export interface EditTreatmentValues extends UpsertTreatmentDto {
  id: number,
  readOnly: boolean
}

interface State {
  currentTreatments: Array<TreatmentDto>,
  selectedTreatment?: TreatmentDto,
  changeTreatments: (treatments: Array<TreatmentDto>) => void,
  changeSelectedTreatment: (treatment?: TreatmentDto) => void,
  addTreatment: (treatment: TreatmentDto) => void,
  editTreatment: (treatment: TreatmentDto) => void,
  deleteTreatment: (selectedTreatment: TreatmentDto) => void,
  modifyTreatmentBillStatus: (billStatus: BillStatus) => void,
  getTreatment: (mode: TreatmentMode) => void,
  toggleUpsertModal: () => void,
  treatmentFormOpen: boolean,
  treatment?: EditTreatmentValues,
  cancelTokenSource: CancelTokenSource
}

const cancelTokenSource = axios.CancelToken.source();

interface Props {
  children: JSX.Element | Array<JSX.Element>
}

export const TreatmentsContext = React.createContext<State>({
  currentTreatments: [],
  changeTreatments: () => {
  },
  changeSelectedTreatment: () => {
  },
  addTreatment: () => {
  },
  editTreatment: () => {
  },
  deleteTreatment: () => {
  },
  toggleUpsertModal: () => {
  },
  getTreatment: () => {
  },
  treatmentFormOpen: false,
  treatment: undefined,
  cancelTokenSource: cancelTokenSource,
  modifyTreatmentBillStatus: () => {
  }
});

export class TreatmentsProvider extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      currentTreatments: [],
      selectedTreatment: undefined,
      changeTreatments: this.changeTreatments,
      changeSelectedTreatment: this.changeSelectedTreatment,
      addTreatment: this.addTreatment,
      editTreatment: this.editTreatment,
      deleteTreatment: this.deleteTreatment,
      modifyTreatmentBillStatus: this.modifyTreatmentBillStatus,
      getTreatment: this.getTreatment,
      treatmentFormOpen: false,
      treatment: undefined,
      toggleUpsertModal: this.toggleUpsertModal,
      cancelTokenSource
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  changeSelectedTreatment = async (treatment?: TreatmentDto) => {
    let treatments = this.state.currentTreatments;
    let treatmentData = treatment;
    if (treatment) {
      treatmentData = await getTreatmentData(treatment.id, cancelTokenSource);
      treatments = treatments.filter(
          t => t.id === treatmentData!.id ? treatmentData : t);
    }
    this.setState({
      currentTreatments: treatments,
      selectedTreatment: treatmentData
    });
  };

  changeTreatments = (treatments: Array<TreatmentDto>) => {
    this.setState({
      currentTreatments: treatments,
      selectedTreatment: undefined
    });
    if (treatments.length) {
      this.changeSelectedTreatment(treatments[0]);
    }
  };

  addTreatment = (treatment: TreatmentDto) => {
    const {currentTreatments} = this.state;
    this.setState({
      currentTreatments: [treatment, ...currentTreatments],
      selectedTreatment: treatment
    });
  };

  editTreatment = (treatment: TreatmentDto) => {

    const {currentTreatments} = this.state;

    const foundTreatmentIndex = currentTreatments.findIndex((currentTreatment) => {
      return currentTreatment.id === treatment.id
    });

    currentTreatments[foundTreatmentIndex] = treatment;

    this.setState({
      currentTreatments: currentTreatments,
      selectedTreatment: treatment
    });
  };

  toggleUpsertModal = () => {
    this.setState(({treatmentFormOpen, treatment}: State) => ({
      treatment: treatmentFormOpen ? undefined : treatment,
      treatmentFormOpen: !treatmentFormOpen
    }));
  };

  getTreatment = (mode: TreatmentMode) => {
    const {cancelTokenSource, selectedTreatment} = this.state;

    if (selectedTreatment) {
      getTreatmentById(selectedTreatment.id, cancelTokenSource)
          .then((response) => {

            const treatment = {
              ...response,
              id: mode == TreatmentMode.COPY ? -1 : selectedTreatment.id,
              costEstimationId: response.costEstimationId ? response.costEstimationId : -1,
              readOnly: mode === TreatmentMode.DETAILS
            };

            if (mode == TreatmentMode.COPY) {
              treatment.unfit.forEach(unfit => {
                (unfit as any).id = undefined;
                unfit.treatmentId = -1;
              });
              treatment.prescriptions.forEach(prescription => {
                prescription.id = undefined;
              })
            }

            this.setState({
              treatment: treatment
            });

            this.toggleUpsertModal();
          }).catch(noop);
    }
  };

  deleteTreatment = (selectedTreatment: TreatmentDto) => {

    const {currentTreatments} = this.state;

    const modifiedTreatments = currentTreatments.filter(treatment => treatment.id != selectedTreatment.id);

    const selectedIndex = modifiedTreatments.length > 0 ? 0 : -1;

    this.setState({
      currentTreatments: [...modifiedTreatments],
      selectedTreatment: modifiedTreatments[selectedIndex]
    });
  };

  modifyTreatmentBillStatus = (billStatus: BillStatus) => {

    const {selectedTreatment, currentTreatments} = this.state;

    if (selectedTreatment) {

      const foundTreatmentIndex = currentTreatments
          .findIndex(treatment => treatment.id == selectedTreatment.id);

      if (foundTreatmentIndex != -1) {

        const modifiedTreatments = Array.from(currentTreatments);

        modifiedTreatments[foundTreatmentIndex] = {
          ...modifiedTreatments[foundTreatmentIndex],
          billStatus: billStatus
        };

        this.setState({
          selectedTreatment: {
            ...selectedTreatment,
            billStatus: billStatus
          },
          currentTreatments: modifiedTreatments
        })
      }
    }
  };

  render() {
    return (
        <TreatmentsContext.Provider value={this.state}>
          {this.props.children}
        </TreatmentsContext.Provider>
    );
  }
}

export const TreatmentsConsumer = TreatmentsContext.Consumer;

export interface TreatmentsConsumerRenderProps extends State {
}

export const withTreatmentsContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <TreatmentsConsumer>
        {({...treatmentProps}: TreatmentsConsumerRenderProps) => {
          return <Component {...props} {...treatmentProps} />
        }}
      </TreatmentsConsumer>
  );
};
